import ReplayIcon from '@mui/icons-material/Replay';
import { FREE_IDEA_LIMIT, ideaAllowance } from '../../services/billing.service';
import React, { useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useHackathon } from '../../hooks/useHackathon';
import { Loading } from '../../components/Loading';
import { useAuth } from '../../services/useAuth';
import { PageContainer } from '../../components/PageContainer';
import { MemberMenu } from '../../components/Hackathon/MemberMenu';
import Stack from '@mui/material/Stack/Stack';
import Button from '@mui/material/Button/Button';
import Box from '@mui/material/Box/Box';
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel';
import Checkbox from '@mui/material/Checkbox/Checkbox';
import TableContainer from '@mui/material/TableContainer/TableContainer';
import Table from '@mui/material/Table/Table';
import TableHead from '@mui/material/TableHead/TableHead';
import TableRow from '@mui/material/TableRow/TableRow';
import TableCell from '@mui/material/TableCell/TableCell';
import TableBody from '@mui/material/TableBody/TableBody';
import { AppBreadcrumbs, PageType } from '../../components/Breadcrumbs/Buttons';
import { JoinTeamMenu } from '../../components/Idea/JoinTeamMenu';
import { RemoveFromTeamButton } from '../../components/Idea/RemoveFromTeamButton';
import Typography from '@mui/material/Typography/Typography';
import TextField from '@mui/material/TextField/TextField';
import Alert from '@mui/material/Alert/Alert';
import { addJudgeToHackathon } from '../../services/hackathon.service';
import { JudgeLinkBox } from '../../components/Judges/JudgeLinkBox';
import { HelpLink } from '../../components/HelpLink';
import { useLiveBoard } from '../../hooks/useLiveSessions';
import { useHackathonSync } from '../../hooks/useHackathonSync';
import { EventControls } from '../../components/Hackathon/EventControls';

export const OrganizerPage = () => {

    const { id } = useParams();

    const { hackathon, loading, error, reload } = useHackathon(id);
    // the people table follows joins and team changes like the hackathon page (Oliver, runs 19-21: someone looked teamless)
    const board = useLiveBoard(id);
    useHackathonSync(id, board.version, hackathon?.participants.length || 0);

    const { user: curUser } = useAuth();
    const [hideJudges, setHideJudges] = React.useState(false);
    const [hideInTeam, setHideInTeam] = React.useState(false);
    const [hideNotInTeam, setHideNotInTeam] = React.useState(false);
    const [copiedEmails, setCopiedEmails] = React.useState(false);
    const [copiedCSV, setCopiedCSV] = React.useState(false);
    const [judgeEmail, setJudgeEmail] = React.useState('');
    const [copiedInvite, setCopiedInvite] = React.useState(false);
    const [addingJudge, setAddingJudge] = React.useState(false);
    const [judgeResult, setJudgeResult] = React.useState<{ severity: 'success' | 'error', message: string } | null>(null);

    const addJudge = async () => {
        if (!hackathon || !judgeEmail.trim()) {
            return;
        }
        setAddingJudge(true);
        setJudgeResult(null);
        try {
            await addJudgeToHackathon(hackathon._id, judgeEmail.trim());
            setJudgeResult({ severity: 'success', message: `${judgeEmail.trim()} is a judge now. Send them the hackathon link; they don't need the invite code.` });
            setJudgeEmail('');
            reload();
        } catch (err: any) {
            setJudgeResult({ severity: 'error', message: err?.response?.data?.message || 'Could not add that judge' });
        } finally {
            setAddingJudge(false);
        }
    }

    const users = useMemo(() => {
        if (!hackathon) {
            return [];
        }

        return hackathon.participants.map(user => {
            const isJudge = hackathon.judges.some(j => j.email === user.email);
            const isOrganizer = hackathon.organizers.some(o => o.email === user.email);
            const team = hackathon.ideas.find(idea => idea.members.some(m => m.email === user.email))?.title;
            return {
                _id: user._id,
                name: user.username,
                email: user.email,
                team: team,
                roles: ['participant', ...(isJudge ? ['judge'] : []), ...(isOrganizer ? ['organizer'] : [])]
            }
        }).filter(user => {
            let shouldHide = false;
            if (hideJudges && !shouldHide) {
                shouldHide = user.roles.includes('judge');
            }
            if (hideInTeam && !shouldHide) {
                shouldHide = !!user.team;
            }
            if (hideNotInTeam && !shouldHide) {
                shouldHide = !user.team;
            }
            return !shouldHide;
        });
    }, [hackathon, hideJudges, hideInTeam, hideNotInTeam])

    const copyEmailAddresses = useCallback(() => {
        const emails = users.map(u => u.email).join(', ');
        navigator.clipboard.writeText(emails);
    }, [users])

    const copyUserCSV = useCallback(() => {
        const csv = users.map(u => [u.name, u.email, u.team || '', ...u.roles].join('\t')).join('\n');
        navigator.clipboard.writeText(csv);
    }, [users])

    if (!id) {
        return <div>No id provided</div>
    }

    if (!curUser) {
        return <div>Not logged in</div>
    }

    // only before the first load: a reload (after opening voting, announcing...) must not unmount the page under the user
    if (loading && !hackathon) {
        return <Loading />
    }

    if (error) {
        console.error(error);
        return <PageContainer>Error loading hackathon</PageContainer>
    }

    if (!hackathon) {
        return <PageContainer>No hackathon found</PageContainer>
    }

    const inviteLink = `${window.location.origin}/h/${hackathon.slug}`;

    if(!hackathon.organizers.some(o => o.email === curUser.email)) {
        return <PageContainer>Not an organizer</PageContainer>
    }

    return (
        <PageContainer>
            <Stack spacing={2}>
                <AppBreadcrumbs crumbs={[
                    {
                        type: PageType.Home,
                        link: '/',
                        name: 'Home'
                    },
                    {
                        type: PageType.Hackathon,
                        link: `/h/${hackathon.slug}`,
                        name: hackathon.name
                    },
                    {
                        type: PageType.Organizer,
                        name: 'Organizer Page'
                    }
                ]} />
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
                    <h1>{hackathon.name} Organizer</h1>
                    {/* the next event is usually this one again: same setup and judges, new dates and people */}
                    <Button variant="outlined" startIcon={<ReplayIcon />} component={Link} to={`/new?from=${hackathon._id}`}>Run it again</Button>
                </Stack>
                <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                        <h2>Invite people</h2>
                        <HelpLink guide="invite">Inviting people</HelpLink>
                    </Stack>
                    {/* what an organizer sends out; it was only findable in the address bar and the edit form */}
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ overflowWrap: 'anywhere' }}>Link: <b>{inviteLink}</b></Typography>
                        <Button size="small" variant="outlined" onClick={() => {
                            navigator.clipboard?.writeText(hackathon.inviteCode ? `${inviteLink}
Invite code: ${hackathon.inviteCode}` : inviteLink).then(() => setCopiedInvite(true));
                        }}>{copiedInvite ? 'Copied' : 'Copy invite'}</Button>
                    </Stack>
                    <Typography color="text.secondary">
                        {hackathon.inviteCode ? <>Invite code: <b>{hackathon.inviteCode}</b>. People enter it after signing in.</> : <>No invite code: anyone with the link can join.</>}
                    </Typography>
                </Stack>
                {/* per-event pricing: what this event's plan is, and the way to upgrade it */}
                <Alert severity={hackathon.plan === 'pro' ? 'success' : 'info'} action={
                    <Button color="inherit" size="small" component={Link} to={`/h/${hackathon.slug}/upgrade`} sx={{ whiteSpace: 'nowrap' }}>
                        {hackathon.plan === 'pro' ? 'View plan' : 'Upgrade'}
                    </Button>
                }>
                    {hackathon.plan === 'pro'
                        ? <>This is a <b>Pro event</b>: your branding shows to everyone in it.</>
                        : <>This event is on the <b>Free</b> plan: up to {FREE_IDEA_LIMIT} ideas ({ideaAllowance(hackathon)?.used ?? 0} so far). Upgrade it to Pro for unlimited ideas and your company's branding.</>}
                </Alert>
                <EventControls hackathon={hackathon} onChange={reload} />
                <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                        <h2>Add a judge</h2>
                        <HelpLink guide="judges">Adding judges</HelpLink>
                    </Stack>
                    <Typography>
                        Judges don't need to join with the invite code: add them by the email they sign in with, then send them the hackathon link.
                        People already in the hackathon can also be made judges from <b>More options</b> in the users table.
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="flex-start" component="form" onSubmit={(e: React.FormEvent) => {
                        e.preventDefault();
                        addJudge();
                    }}>
                        <TextField size="small" label="Judge's email" type="email" value={judgeEmail} sx={{ minWidth: 280 }}
                            onChange={(e) => setJudgeEmail(e.target.value)} />
                        <Button type="submit" variant="contained" disabled={addingJudge || !judgeEmail.trim()}>Add judge</Button>
                    </Stack>
                    {judgeResult && <Alert severity={judgeResult.severity}>{judgeResult.message}</Alert>}
                    <JudgeLinkBox hackathonId={hackathon._id} slug={hackathon.slug} />
                </Stack>
                <Stack spacing={2}>
                    <Stack direction="row" alignItems='center' spacing={1}>

                        <h2>Users ({users.length})</h2>
                        <Box flex={1}></Box>
                        <FormControlLabel control={<Checkbox checked={hideJudges} onChange={(e) => setHideJudges(e.target.checked)} />} label={'Hide judges'} />
                        <FormControlLabel control={<Checkbox checked={hideInTeam} onChange={(e) => setHideInTeam(e.target.checked)} />} label={'Hide in team'} />
                        <FormControlLabel control={<Checkbox checked={hideNotInTeam} onChange={(e) => setHideNotInTeam(e.target.checked)} />} label={'Hide not in team'} />

                        <Button variant="contained"
                            disabled={copiedEmails}
                            onClick={() => {
                                setCopiedEmails(true);
                                copyEmailAddresses();
                                setTimeout(() => {
                                    setCopiedEmails(false);
                                }, 2000)
                            }}>{copiedEmails ? 'Copied' : 'Copy email addresses'}</Button>

                        <Button variant="outlined" color="secondary"
                            disabled={copiedCSV}
                            onClick={() => {
                                setCopiedCSV(true);
                                copyUserCSV();
                                setTimeout(() => {
                                    setCopiedCSV(false);
                                }, 2000);
                            }}>{copiedCSV ? 'Copied' : 'Copy Data Table'}</Button>
                    </Stack>
                    <TableContainer>
                        <Table aria-label="Users table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Team</TableCell>
                                    <TableCell>Roles</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map(user => {
                                    const isJudge = user.roles.includes('judge');
                                    const isOrganizer = user.roles.includes('organizer');
                                    return (
                                        <TableRow key={user.email}>
                                            <TableCell>{user.name}</TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>
                                                {
                                                    user.team && <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap>
                                                        <Typography>{user.team}</Typography>
                                                        <RemoveFromTeamButton userEmail={user.email} hackathon={hackathon} onChange={() => {
                                                            reload();
                                                        }} />
                                                        {/* organizers move people at any time, team locks or not */}
                                                        <JoinTeamMenu userEmail={user.email} hackathon={hackathon} label="Move to team" onChange={() => {
                                                            reload();
                                                        }} />
                                                    </Stack>
                                                }

                                                {
                                                    !user.team && <JoinTeamMenu userEmail={user.email} hackathon={hackathon} onChange={() => {
                                                        reload();
                                                    }} />
                                                }

                                            </TableCell>
                                            <TableCell>

                                                {user.roles.join(', ')}

                                            </TableCell>
                                            <TableCell>
                                                <Stack spacing={1} direction="row">
                                                    <MemberMenu user={user} hackathon={hackathon} actions={
                                                        [
                                                            isJudge ? 'removeJudge' : 'addJudge',
                                                            isOrganizer ? 'removeOrganizer' : 'addOrganizer',
                                                            'removeParticipant'
                                                        ]
                                                    } />

                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>
            </Stack>
        </PageContainer>
    )
}